import { useState } from 'react';
import { MessageSquare, Check, AlertCircle, Loader2 } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { useAuth } from '../../context/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MONO = { fontFamily: "'IBM Plex Mono', monospace" };
const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };

const CATEGORIES = [
  { id: 'general',          label: 'General Inquiry' },
  { id: 'billing',          label: 'Billing & Subscription' },
  { id: 'technical',        label: 'Technical Issue' },
  { id: 'candidate',        label: 'Candidate / Gallery Issue' },
  { id: 'dedicated_hiring', label: 'Dedicated Hiring Service' },
  { id: 'other',            label: 'Other' },
];

const Support = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ subject: '', category: 'general', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState(null);
  const [loadingTickets, setLoadingTickets] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      setError('Please fill in subject and message.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${BACKEND}/api/support/ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setForm({ subject: '', category: 'general', message: '' });
      } else {
        setError(data.detail || 'Failed to submit ticket.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTickets = async () => {
    setLoadingTickets(true);
    try {
      const res = await authFetch(`${BACKEND}/api/support/tickets`);
      const data = await res.json();
      if (data.success) setTickets(data.data);
    } catch { /* silent */ }
    finally { setLoadingTickets(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1" style={SERIF}>Support</h1>
        <p className="text-sm text-muted-foreground">Report an issue or get help from our team. We respond within 24 hours.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form */}
        <div className="lg:col-span-2">
          {submitted ? (
            <div className="border border-green-200 bg-green-50 p-8 text-center space-y-3">
              <div className="w-10 h-10 bg-green-100 border border-green-200 flex items-center justify-center mx-auto">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold">Ticket submitted</h3>
              <p className="text-sm text-muted-foreground">We'll review your request and respond to <strong>{user?.email}</strong> within 24 hours.</p>
              <button
                onClick={() => setSubmitted(false)}
                className="text-sm text-foreground underline mt-2"
              >
                Submit another ticket
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="border border-border bg-white" data-testid="support-form">
              <div className="px-6 py-4 border-b border-border">
                <p className="text-xs uppercase tracking-widest text-muted-foreground" style={MONO}>New Support Ticket</p>
              </div>
              <div className="p-6 space-y-5">
                {error && (
                  <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2" style={MONO}>Category</label>
                  <select
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full border border-border bg-[hsl(var(--secondary))] px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
                    data-testid="support-category-select"
                  >
                    {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2" style={MONO}>Subject</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="Brief description of your issue"
                    className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
                    data-testid="support-subject-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2" style={MONO}>Message</label>
                  <textarea
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Please describe the issue in detail — include steps to reproduce if it's a technical issue"
                    rows={6}
                    className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground resize-none"
                    data-testid="support-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-foreground text-background text-sm font-medium hover:bg-foreground/90 disabled:opacity-60 transition-colors"
                  data-testid="support-submit-btn"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <>Submit ticket <MessageSquare className="w-4 h-4" /></>}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Sidebar info */}
        <div className="space-y-5">
          <div className="border border-border p-5">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3" style={MONO}>Response Times</p>
            <div className="space-y-2.5">
              {[
                { cat: 'General / Billing', time: '24h' },
                { cat: 'Technical issues', time: '12h' },
                { cat: 'Dedicated Hiring', time: '4h' },
              ].map(r => (
                <div key={r.cat} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{r.cat}</span>
                  <span className="font-medium">{r.time}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-border p-5">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3" style={MONO}>Quick Links</p>
            <div className="space-y-2">
              {[
                { label: 'View pricing plans', href: '/pricing' },
                { label: 'Manage subscription', href: '/dashboard/account' },
                { label: 'Platform documentation', href: '/for-talent' },
              ].map(l => (
                <a key={l.label} href={l.href} className="block text-sm text-foreground hover:text-[hsl(var(--primary))] transition-colors">
                  → {l.label}
                </a>
              ))}
            </div>
          </div>

          {/* Previous tickets toggle */}
          <div className="border border-border">
            <button
              onClick={() => { if (!tickets) loadTickets(); else setTickets(null); }}
              className="w-full px-5 py-3 text-left text-xs uppercase tracking-widest text-muted-foreground flex items-center justify-between"
              style={MONO}
            >
              My Previous Tickets
              <span>{tickets ? '▲' : '▼'}</span>
            </button>
            {tickets && (
              <div className="divide-y divide-border">
                {loadingTickets && <p className="px-5 py-4 text-sm text-muted-foreground">Loading...</p>}
                {!loadingTickets && tickets.length === 0 && <p className="px-5 py-4 text-sm text-muted-foreground">No tickets yet.</p>}
                {tickets.map(t => (
                  <div key={t.id} className="px-5 py-3">
                    <p className="text-sm font-medium truncate">{t.subject}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-1.5 py-0.5 ${
                        t.status === 'open' ? 'bg-blue-50 text-blue-700' :
                        t.status === 'resolved' ? 'bg-green-50 text-green-700' :
                        'bg-secondary text-muted-foreground'
                      }`} style={MONO}>{t.status}</span>
                      <span className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                    {t.admin_reply && (
                      <p className="text-xs text-muted-foreground mt-1.5 border-l-2 border-[hsl(var(--primary)/0.4)] pl-2 italic">{t.admin_reply}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
